import SimcaptureUIComponentsThemeProvider from '@blinemedical/simcapture-ui-components/theme/provider';
import {createTheme, makeStyles, StylesProvider, ThemeProvider} from '@material-ui/core/styles';
import axios from 'axios';
import {useFlags} from 'launchdarkly-react-client-sdk';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import React, {lazy, Suspense, useEffect, useMemo, useState} from 'react';
import {IntlProvider} from 'react-intl';
import {Redirect, Route, Switch, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'redux-store';

import './fonts.css';

import {fetchLookups} from 'actions/session-actions';
import UiConfig from 'constants/ui-config';
import {AccessibilityContext} from 'contexts/accessibility-context';
import useIntlMessages from 'hooks/use-intl-messages';
import {anonymousRoutePaths} from 'views/anonymous/anonymous-routes';
import {
    ACCESSIBILITY_DEFAULT_VALUES,
    ACCESSIBILITY_LOCAL_STORAGE_KEY,
} from 'views/signed-in/accessibility/accessibility-constants';

const AnonymousRoutes = lazy(() => import(/* webpackChunkName: "anonymous" */ 'views/anonymous/anonymous-routes'));
const SignedInRoutes = lazy(() => import(/* webpackChunkName: "signed-in" */ 'views/signed-in/signed-in-routes'));
const CenterSignInKioskRoutes = lazy(
    () => import(/* webpackChunkName: "center-sign-in-kiosk" */ 'views/csi-kiosk/csi-kiosk-routes'),
);
const ExamsRoutes = lazy(() => import(/* webpackChunkName: "exams" */ 'views/exams/exams-routes'));
const ClusterRoutes = lazy(() => import(/* webpackChunkName: "cluster" */ 'views/cluster/cluster-routes'));
const KitchenSinkRoutes = lazy(
    () => import(/* webpackChunkName: "kitchen-sink" */ 'views/kitchen-sink/kitchen-sink-routes'),
);
const AuthorizedAnonymousRoutes = lazy(
    () =>
        import(/* webpackChunkName: "authorized-anonymous" */ 'views/authorized-anonymous/authorized-anonymous-routes'),
);

import CssBaseline from 'css-baseline';
import componentProps from 'mui/component-props';
import mixins from 'mui/mixins/mixins';
import getOverrides from 'mui/overrides';
import {PaletteLight} from 'mui/palette';
import shadows from 'mui/shadows';
import {transitionsNone, transitionsNormal} from 'mui/transitions';
import getTypography from 'mui/typography';
import {createAxiosRetryInterceptor} from 'utils/api-utils';
import * as AppcuesUtils from 'utils/appcues-utils';
import {useHeap} from 'utils/heap-io-utils';
import IntlUtils from 'utils/intl-utils';
import Logger from 'utils/logger';
import MeteorCookies from 'utils/meteor-cookies';

const showComponents = UiConfig.SHOW_COMPONENTS ?? false;

const logger = new Logger('app-routes');
const BASE_RETRY_DELAY = 100;
const MAX_NETWORK_ERROR_RETRIES = 2;

const AppRoutes = React.memo(() => {
    const dispatch = useDispatch();
    const client = useSelector((state) => state.session.client);
    const lookups = useSelector((state) => state.session.lookups);
    const loggedInUser = useSelector((state) => state.session.user);
    const settings = useSelector((state) => state.session.settings);
    const formats = useSelector((state) => state.intl.formats);
    const messages = useSelector((state) => state.intl.messages);
    const hourTwelve = settings.get('hourTwelve');
    const locale = settings.get('locale');
    const timezone = settings.get('timezone');

    // Use JS object so values can be compared in useMemo
    const [currentAccessibilitySettings, setCurrentAccessibilitySettings] = useState(() => {
        // If no value is found, set to defaults
        if (isNil(localStorage.getItem(ACCESSIBILITY_LOCAL_STORAGE_KEY))) {
            localStorage.setItem(ACCESSIBILITY_LOCAL_STORAGE_KEY, JSON.stringify(ACCESSIBILITY_DEFAULT_VALUES));
        }
        // Parse JSON string back to JSON object
        const currentValues = JSON.parse(localStorage.getItem(ACCESSIBILITY_LOCAL_STORAGE_KEY));
        const accessibilityKeys = Object.keys(ACCESSIBILITY_DEFAULT_VALUES);
        const currentValuesFiltered = Object.fromEntries(
            Object.entries(currentValues).filter(([key]) => accessibilityKeys.includes(key)),
        );

        // Overwrite default values so that any new settings are defaulted and existing settings are kept
        return Object.assign({}, ACCESSIBILITY_DEFAULT_VALUES, currentValuesFiltered);
    });

    const location = useLocation();
    const {globalAxiosRequestRetries, globalRequestRetriesDelayTime, globalRequestRetriesRetryLimit} = useFlags();

    const isLookupsLoaded = !isNil(lookups);
    const isMessagesLoaded = useIntlMessages('global');
    const isLoaded = isLookupsLoaded && isMessagesLoaded;

    useEffect(() => {
        if (globalAxiosRequestRetries) {
            const delayTime = globalRequestRetriesDelayTime || BASE_RETRY_DELAY;
            const retryLimit = globalRequestRetriesRetryLimit || MAX_NETWORK_ERROR_RETRIES;
            axios.interceptors.response.use(...createAxiosRetryInterceptor(0, delayTime, retryLimit));
        }
    }, []);

    useEffect(() => {
        AppcuesUtils.identifyUser(window, logger, {
            client,
            user: loggedInUser,
            language: locale,
            version: __VERSION__,
        });
    }, [client.get('clientId'), loggedInUser.get('userId'), locale]);

    useHeap(window, {logger, clientId: client.get('clientId'), userId: loggedInUser.get('userId'), locale});

    useEffect(() => {
        AppcuesUtils.trackPageChange(window, logger);
    }, [location.pathname]);

    useEffect(() => {
        if (!isLookupsLoaded) {
            const failureCallback = (err) => {
                console.error('Failed to fetch lookups:', err);
            };
            dispatch(fetchLookups(noop, failureCallback));
        }
    }, [isLookupsLoaded]);

    // Reloads locale messages when the locale changes. This can cause a race condition with any views that are already
    // loading locale messages for the previous locale when the locale changes.
    useEffect(() => {
        console.debug(`Locale settings changed to ${locale}. Reloading locale messages.`);
        IntlUtils.reloadLocaleMessages(locale);
    }, [locale]);

    // Reloads time formats when the hourTwelve or timezone settings change.
    useEffect(() => {
        console.debug('Time/timezone settings changed. Updating time formats.');
        IntlUtils.updateTimeFormats(hourTwelve, timezone);
    }, [hourTwelve, timezone]);

    const cookies = MeteorCookies.getInstance();

    const isAnonymous = isNil(cookies.get('token'));

    const handleSetAccessibilitySettings = (updatedAccessibilitySettings) => {
        // Maintain compatibility with values that do not contain all settings
        const nextValues = Object.assign(currentAccessibilitySettings, updatedAccessibilitySettings);
        // Spread values to trigger state update and a rerender
        setCurrentAccessibilitySettings({...nextValues});
        // Save to locale storage
        localStorage.setItem(ACCESSIBILITY_LOCAL_STORAGE_KEY, JSON.stringify(nextValues));
    };

    const typographyOptions = useMemo(() => getTypography(PaletteLight), []);

    const transitions = useMemo(
        () => (currentAccessibilitySettings.reduceMotion ? transitionsNone : transitionsNormal),
        [currentAccessibilitySettings.motion],
    );

    const overrides = useMemo(() => {
        return getOverrides(typographyOptions);
    }, [typographyOptions]);

    const theme = useMemo(() => {
        return createTheme({
            mixins,
            overrides,
            palette: PaletteLight,
            props: componentProps,
            shadows,
            spacing: (factor) => `${10 * factor}px`, // spacing in multiples of 10px
            transitions,
            typography: typographyOptions,
        });
    }, [overrides, transitions, typographyOptions]);

    return (
        <IntlProvider locale={locale} messages={messages} formats={formats} textComponent='span'>
            <StylesProvider injectFirst>
                <ThemeProvider theme={theme}>
                    <SimcaptureUIComponentsThemeProvider
                        paletteMode='light'
                        disableTransitions={currentAccessibilitySettings.reduceMotion}>
                        <AccessibilityContext.Provider
                            value={{
                                accessibilitySettings: currentAccessibilitySettings,
                                setAccessibilitySettings: handleSetAccessibilitySettings,
                            }}>
                            <CssBaseline />
                            <AppRoutesContent isLoaded={isLoaded} isAnonymous={isAnonymous} />
                        </AccessibilityContext.Provider>
                    </SimcaptureUIComponentsThemeProvider>
                </ThemeProvider>
            </StylesProvider>
        </IntlProvider>
    );
});

const useStyles = makeStyles((theme) => ({
    app: {
        ...theme.mixins.legacyTypography.mainFont,
        background: theme.mixins.legacyColors.backgroundLightGray.background,
    },
    viewLayer: theme.mixins.legacyLayout.isolate,
}));

type AppRoutesContentProps = {
    isLoaded: boolean;
    isAnonymous: boolean;
};

const AppRoutesContent = (props: AppRoutesContentProps) => {
    const {isLoaded, isAnonymous} = props;
    const classes = useStyles();
    const location = useLocation();

    return (
        <div className={classes.app}>
            <div className={classes.viewLayer}>
                {isLoaded && (
                    <Switch>
                        {showComponents && (
                            <Route path='/components/'>
                                <Suspense fallback={null}>
                                    <KitchenSinkRoutes />
                                </Suspense>
                            </Route>
                        )}
                        <Route path='/center-sign-in/kiosk/'>
                            <Suspense fallback={null}>
                                <CenterSignInKioskRoutes />
                            </Suspense>
                        </Route>
                        <Route path='/exams'>
                            <Suspense fallback={null}>
                                <ExamsRoutes />
                            </Suspense>
                        </Route>
                        <Route path='/cluster'>
                            <Suspense fallback={null}>
                                <ClusterRoutes />
                            </Suspense>
                        </Route>
                        <Route path='/session-viewer'>
                            <Suspense fallback={null}>
                                <AuthorizedAnonymousRoutes />
                            </Suspense>
                        </Route>
                        <Route path={anonymousRoutePaths} exact>
                            <Suspense fallback={null}>
                                <AnonymousRoutes />
                            </Suspense>
                        </Route>
                        {isAnonymous && (
                            <Redirect
                                to={{
                                    pathname: '/login',
                                    state: {nextLocation: location},
                                }}
                            />
                        )}
                        {!isAnonymous && (
                            <Suspense fallback={null}>
                                <SignedInRoutes />
                            </Suspense>
                        )}
                    </Switch>
                )}
            </div>
        </div>
    );
};

export default AppRoutes;
